import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Avatar,
  Paper,
  Stack,
  Chip,
  Rating,
  Button,
  Fade,
  Zoom,
} from '@mui/material';
import {
  EmojiEmotions,
  Handshake,
  Star,
  People,
  Verified,
  Diamond,
  Spa,
  BusinessCenter,
  Security,
  AccessTime,
  Timeline,
  Hotel,
  Build,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Section border style - matching homepage and other pages
const sectionBorderStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '4px',
  background: 'linear-gradient(90deg, #9b4d3c 0%, #d49b7b 50%, #e0e0e0 100%)',
  zIndex: 1,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Title style with moving underline
const titleWithUnderlineStyle = {
  fontWeight: 600,
  color: '#333',
  fontSize: { xs: '2rem', md: '2.5rem' },
  position: 'relative',
  display: 'inline-block',
  pb: 2,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
    borderRadius: 2,
    animation: 'pulseWidth 2s ease-in-out infinite',
  }
};

const AboutPage = () => {
  const values = [
    {
      icon: <Diamond sx={{ fontSize: 40 }} />,
      title: 'Excellence',
      description: 'We strive for excellence in every property we manage and every guest experience we create.',
    },
    {
      icon: <Handshake sx={{ fontSize: 40 }} />,
      title: 'Integrity',
      description: 'Honest, transparent dealings with property owners, guests, and partners.',
    },
    {
      icon: <People sx={{ fontSize: 40 }} />,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We go above and beyond for our clients.',
    },
    {
      icon: <Verified sx={{ fontSize: 40 }} />,
      title: 'Quality Assurance',
      description: 'Rigorous standards for property maintenance and guest services.',
    },
  ];

  const team = [
    {
      name: 'John Smith',
      position: 'Founder & CEO',
      bio: '15+ years in real estate and hospitality management',
      avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop',
    },
    {
      name: 'Sarah Johnson',
      position: 'Head of Real Estate',
      bio: 'Expert in luxury property sales and acquisitions',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop',
    },
    {
      name: 'Michael Chen',
      position: 'Hospitality Director',
      bio: 'Ensuring exceptional guest experiences',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop',
    },
    {
      name: 'Emily Rodriguez',
      position: 'Maintenance Manager',
      bio: 'Leading our team of skilled professionals',
      avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop',
    },
  ];

  const milestones = [
    {
      year: '2011',
      title: 'The Beginning',
      description: 'Marvellous founded with a vision to bridge real estate and hospitality',
      icon: <Timeline />,
    },
    {
      year: '2015',
      title: 'Expansion to Pune',
      description: 'Extended our presence to Pune\'s luxury property market',
      icon: <BusinessCenter />,
    },
    {
      year: '2018',
      title: 'Corporate Hospitality',
      description: 'Launched dedicated corporate housing division',
      icon: <Hotel />,
    },
    {
      year: '2021',
      title: 'Services Division',
      description: 'Added professional property maintenance services',
      icon: <Build />,
    },
    {
      year: '2024',
      title: '500+ Properties',
      description: 'Reached milestone of managing over 500 luxury properties',
      icon: <Verified />,
    },
  ];

  const testimonials = [
    { name: 'Rajesh Mehta', role: 'Property Owner, Mumbai', quote: 'Marvellous transformed my property into a profitable investment. Their team is professional and responsive.', rating: 5 },
    { name: 'Priya Sharma', role: 'Corporate Guest, Pune', quote: 'The corporate suite was perfect for my extended stay. Impeccable service and attention to detail.', rating: 5 },
    { name: 'Amit Desai', role: 'Real Estate Investor', quote: 'Found my dream investment property through them. Their market expertise is unmatched.', rating: 5 },
  ];

  return (
    <Box sx={{ bgcolor: '#f8f5f0', minHeight: '100vh' }}>
      {/* SECTION 1: HERO - No top border (like homepage) */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 6, 
          mb: 0,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #9b4d3c 0%, #d49b7b 100%)',
          color: 'white',
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in={true} timeout={1000}>
            <Box>
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                About Marvellous
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  maxWidth: 800, 
                  mx: 'auto', 
                  mb: 4,
                  opacity: 0.95,
                }}
              >
                Redefining Luxury Living Through Integrated Real Estate & Hospitality Excellence
              </Typography>
              <Box 
                sx={{ 
                  width: 100, 
                  height: 4, 
                  mx: 'auto', 
                  bgcolor: 'white',
                  opacity: 0.5,
                  borderRadius: 2,
                }} 
              />
            </Box>
          </Fade>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          }}
        />
      </Paper>

      {/* SECTION 2: OUR STORY - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Paper 
            elevation={2}
            sx={{ 
              p: { xs: 3, md: 6 }, 
              borderRadius: 4,
              bgcolor: 'white',
              position: 'relative',
              overflow: 'hidden',
              border: '2px solid #9b4d3c',
            }}
          >
            {/* Decorative accent line */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: 4,
                background: 'linear-gradient(90deg, #9b4d3c, #d49b7b, #9b4d3c)',
              }}
            />
            
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                {/* Title with icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: '#9b4d3c', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    <Timeline />
                  </Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      color: '#9b4d3c',
                      fontWeight: 600,
                    }}
                  >
                    Our Journey
                  </Typography>
                </Box>
                
                {/* Content with improved typography */}
                <Box sx={{ mt: 3 }}>
                  <Typography 
                    variant="body1" 
                    paragraph 
                    sx={{ 
                      fontSize: '1.1rem', 
                      lineHeight: 1.8,
                      color: '#333',
                      position: 'relative',
                      pl: 2,
                      borderLeft: '3px solid',
                      borderLeftColor: '#d49b7b',
                    }}
                  >
                    It began on a rainy Mumbai evening in 2011. Our founder sat with an elderly couple selling their family home of forty years. Every agent they'd met talked about square footage and market price. That evening, no one talked about numbers. They talked about Diwali celebrations in the living room. Children learning to walk in the garden. Sunday mornings with chai drifting through the kitchen.
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    paragraph 
                    sx={{ 
                      fontSize: '1.1rem', 
                      lineHeight: 1.8,
                      color: '#333',
                      fontWeight: 500,
                      bgcolor: '#fdf8f5',
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'rgba(155,77,60,0.1)',
                    }}
                  >
                    That night, Marvellous was born—from a simple truth the industry had forgotten: a home is never just walls and a roof. It's the keeper of a family's most precious moments.
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    paragraph 
                    sx={{ 
                      fontSize: '1.1rem', 
                      lineHeight: 1.8,
                      color: '#333',
                    }}
                  >
                    Today, that truth guides everything we do. When a young couple in Pune walks into their first apartment, we don't just hand over keys. We see the life they'll build together. When a corporate executive checks into our suite for three months, we create a space where "away from home" doesn't feel lonely. When a property owner trusts us with their legacy, we treat it as our own.
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    paragraph 
                    sx={{ 
                      fontSize: '1.1rem', 
                      lineHeight: 1.8,
                      color: '#333',
                      fontStyle: 'italic',
                    }}
                  >
                    Over a thousand families have become part of our story. Not as clients or transactions. As people who reminded us why we started this journey in the first place.
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      mt: 3, 
                      p: 2, 
                      background: 'linear-gradient(135deg, #9b4d3c 0%, #d49b7b 100%)',
                      borderRadius: 2,
                      color: 'white',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      "Because at Marvellous, we believe every life deserves to be lived marvellously."
                    </Typography>
                  </Box>
                </Box>
                
                {/* Stats chips */}
                <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', gap: 1 }}>
                  <Chip 
                    icon={<EmojiEmotions />} 
                    label="14+ Years" 
                    sx={{ 
                      bgcolor: '#9b4d3c', 
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' },
                    }} 
                  />
                  <Chip 
                    icon={<Star />} 
                    label="4.9 Rating" 
                    sx={{ 
                      bgcolor: '#d49b7b', 
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' },
                    }} 
                  />
                  <Chip 
                    icon={<Verified />} 
                    label="1000+ Families" 
                    sx={{ 
                      bgcolor: '#4caf50', 
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' },
                    }} 
                  />
                </Stack>
              </Grid>
              
              <Grid item xs={12} md={6}>
                {/* Image with overlay */}
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&h=400&fit=crop"
                    alt="Luxury Property"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 4,
                      boxShadow: 8,
                      border: '2px solid #9b4d3c',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      left: 20,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      p: 2,
                      borderRadius: 2,
                      boxShadow: 4,
                      maxWidth: 200,
                      border: '2px solid #d49b7b',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#9b4d3c' }}>
                      "A home is where your story begins."
                    </Typography>
                  </Box>
                </Box>
                
                {/* Quick stats cards */}
                <Grid container spacing={2} sx={{ mt: 3 }}>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: '#f8f5f0', 
                      borderRadius: 2,
                      border: '2px solid #9b4d3c',
                      transition: 'transform 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}>
                      <Typography variant="h4" sx={{ color: '#9b4d3c', fontWeight: 700 }}>500+</Typography>
                      <Typography variant="body2" color="text.secondary">Properties Managed</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: '#f8f5f0', 
                      borderRadius: 2,
                      border: '2px solid #9b4d3c',
                      transition: 'transform 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}>
                      <Typography variant="h4" sx={{ color: '#9b4d3c', fontWeight: 700 }}>Mumbai</Typography>
                      <Typography variant="body2" color="text.secondary">Pune</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* SECTION 3: MILESTONES - With thicker gradient top border and moving underline */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={titleWithUnderlineStyle}
              >
                Our Milestones
              </Typography>
            </Box>
          </Fade>
          
          <Grid container spacing={3}>
            {milestones.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in={true} timeout={1000 + index * 100}>
                  <Box sx={{ 
                    p: 3, 
                    border: '2px solid #9b4d3c',
                    borderRadius: 3,
                    height: '100%',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                      borderColor: '#d49b7b',
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: '#9b4d3c' }}>{item.icon}</Avatar>
                      <Typography variant="h4" sx={{ color: '#9b4d3c', fontWeight: 700 }}>
                        {item.year}
                      </Typography>
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SECTION 4: OUR VALUES - With thicker gradient top border and moving underline */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={titleWithUnderlineStyle}
              >
                Our Core Values
              </Typography>
              <Typography variant="h6" align="center" color="text.secondary" sx={{ mt: 2 }}>
                The principles that guide everything we do
              </Typography>
            </Box>
          </Fade>
          
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Fade in={true} timeout={1000 + index * 200}>
                  <Card 
                    elevation={3}
                    sx={{ 
                      height: '100%', 
                      textAlign: 'center', 
                      p: 3,
                      borderRadius: 3,
                      border: '2px solid #9b4d3c',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 8,
                        borderColor: '#d49b7b',
                      },
                    }}
                  >
                    <Box sx={{ color: '#9b4d3c', mb: 2, fontSize: 50 }}>
                      {value.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value.description}
                    </Typography>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SECTION 5: WHY CHOOSE MARVELLOUS - With thicker gradient top border and moving underline */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={titleWithUnderlineStyle}
              >
                Why Choose Marvellous?
              </Typography>
            </Box>
          </Fade>
          
          <Paper 
            elevation={2}
            sx={{ 
              p: { xs: 4, md: 6 }, 
              borderRadius: 4,
              bgcolor: 'white',
              border: '2px solid #d49b7b',
            }}
          >
            <Grid container spacing={3}>
              {[
                { icon: <Verified />, title: '15+ Years Expertise', desc: 'Deep market knowledge and proven track record' },
                { icon: <People />, title: 'Personalized Service', desc: 'Dedicated relationship managers for every client' },
                { icon: <Security />, title: 'Secure Transactions', desc: '100% safe and transparent dealings' },
                { icon: <AccessTime />, title: '24/7 Support', desc: 'Round-the-clock assistance for all needs' },
                { icon: <Diamond />, title: 'Premium Portfolio', desc: 'Only the finest properties in prime locations' },
                { icon: <Handshake />, title: 'Client First Approach', desc: 'Your satisfaction is our top priority' },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Fade in={true} timeout={1000 + index * 100}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      border: '2px solid #9b4d3c',
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: 'rgba(155,77,60,0.05)',
                        borderColor: '#d49b7b',
                        transform: 'translateX(5px)',
                      },
                    }}>
                      <Avatar sx={{ bgcolor: '#9b4d3c', width: 50, height: 50 }}>
                        {item.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* SECTION 6: TESTIMONIALS - With thicker gradient top border and moving underline */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={titleWithUnderlineStyle}
              >
                What Our Clients Say
              </Typography>
              <Typography variant="h6" align="center" color="text.secondary" sx={{ mt: 2 }}>
                Real experiences from property owners and guests
              </Typography>
            </Box>
          </Fade>
          
          <Grid container spacing={4}>
            {testimonials.map((client, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Zoom in={true} timeout={1000 + index * 200}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%', 
                      p: 3,
                      borderRadius: 3,
                      bgcolor: 'white',
                      border: '2px solid #9b4d3c',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 8,
                        borderColor: '#d49b7b',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: '#9b4d3c', mr: 2, width: 50, height: 50 }}>
                        {client.name[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">{client.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{client.role}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" paragraph sx={{ fontStyle: 'italic', minHeight: 80 }}>
                      "{client.quote}"
                    </Typography>
                    <Rating value={client.rating} readOnly size="small" sx={{ color: '#d49b7b' }} />
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SECTION 7: TEAM - With thicker gradient top border and moving underline */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={titleWithUnderlineStyle}
              >
                Meet Our Leadership
              </Typography>
              <Typography variant="h6" align="center" color="text.secondary" sx={{ mt: 2 }}>
                Dedicated professionals committed to your satisfaction
              </Typography>
            </Box>
          </Fade>
          
          <Grid container spacing={4}>
            {team.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Zoom in={true} timeout={1000 + index * 200}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%', 
                      textAlign: 'center',
                      borderRadius: 3,
                      border: '2px solid #9b4d3c',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 8,
                        borderColor: '#d49b7b',
                      },
                    }}
                  >
                    <Avatar
                      src={member.avatar}
                      sx={{
                        width: 140,
                        height: 140,
                        mx: 'auto',
                        mt: 3,
                        border: 4,
                        borderColor: '#9b4d3c',
                      }}
                    />
                    <CardContent>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                        {member.name}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: '#9b4d3c', fontWeight: 500 }} gutterBottom>
                        {member.position}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                        {member.bio}
                      </Typography>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SECTION 8: PRESENCE - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Paper 
            elevation={3}
            sx={{ 
              p: { xs: 4, md: 6 }, 
              borderRadius: 4,
              background: 'linear-gradient(135deg, #9b4d3c 0%, #d49b7b 100%)',
              color: 'white',
              border: '2px solid #9b4d3c',
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    color: 'white',
                    fontWeight: 600,
                    position: 'relative',
                    display: 'inline-block',
                    pb: 2,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: 80,
                      height: 3,
                      background: 'white',
                      borderRadius: 2,
                      opacity: 0.5,
                      animation: 'pulseWidth 2s ease-in-out infinite',
                    }
                  }}
                >
                  Serving Mumbai & Pune
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, opacity: 0.95, mt: 3 }}>
                  With a strong presence in Maharashtra's two most dynamic cities, we've established ourselves as the go-to partner for luxury real estate and hospitality. From the bustling streets of Mumbai to the serene landscapes of Pune, our portfolio spans the region's most prestigious addresses.
                </Typography>
                <Stack direction="row" spacing={5} sx={{ mt: 4 }}>
                  <Box>
                    <Typography variant="h2" sx={{ fontWeight: 700, fontSize: { xs: '2.5rem', md: '3rem' } }}>45+</Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>Properties in Mumbai</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h2" sx={{ fontWeight: 700, fontSize: { xs: '2.5rem', md: '3rem' } }}>32+</Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>Properties in Pune</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <BusinessCenter sx={{ fontSize: 80, opacity: 0.9 }} />
                    <Typography variant="h6">Corporate</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Spa sx={{ fontSize: 80, opacity: 0.9 }} />
                    <Typography variant="h6">Luxury</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Security sx={{ fontSize: 80, opacity: 0.9 }} />
                    <Typography variant="h6">Trusted</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* SECTION 9: MISSION STATEMENT - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Paper 
            elevation={2}
            sx={{ 
              p: { xs: 4, md: 6 }, 
              bgcolor: '#fff9f5',
              textAlign: 'center', 
              borderRadius: 4,
              border: '2px solid #d49b7b',
            }}
          >
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                color: '#9b4d3c',
                fontWeight: 600,
                position: 'relative',
                display: 'inline-block',
                pb: 2,
                mb: 3,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 80,
                  height: 3,
                  background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
                  borderRadius: 2,
                  animation: 'pulseWidth 2s ease-in-out infinite',
                }
              }}
            >
              Our Promise
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                maxWidth: 900, 
                mx: 'auto', 
                fontStyle: 'italic', 
                color: '#555', 
                lineHeight: 1.8,
                fontWeight: 400,
              }}
            >
              "To transform the way people experience luxury living by seamlessly integrating world-class real estate, exceptional hospitality, and impeccable service — creating spaces where life's finest moments unfold."
            </Typography>
            <Stack 
              direction="row" 
              spacing={2} 
              justifyContent="center" 
              sx={{ mt: 4, flexWrap: 'wrap', gap: 1 }}
            >
              <Chip label="Since 2011" sx={{ bgcolor: '#9b4d3c', color: 'white' }} />
              <Chip label="Mumbai • Pune" sx={{ bgcolor: '#d49b7b', color: 'white' }} />
              <Chip label="1000+ Happy Clients" sx={{ bgcolor: '#4caf50', color: 'white' }} />
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* SECTION 10: CALL TO ACTION - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Paper 
            elevation={4}
            sx={{ 
              p: { xs: 4, md: 6 }, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #9b4d3c 0%, #d49b7b 100%)',
              color: 'white', 
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
              border: '2px solid #9b4d3c',
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                Ready to Experience Marvellous?
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, maxWidth: 700, mx: 'auto' }}>
                Whether you're looking to buy, sell, rent, or need property services
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
                <Button 
                  component={Link}
                  to="/contact"
                  variant="contained" 
                  size="large" 
                  sx={{ 
                    bgcolor: 'white', 
                    color: '#9b4d3c', 
                    px: 4,
                    py: 1.5,
                    '&:hover': { 
                      bgcolor: 'rgba(255,255,255,0.9)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'transform 0.3s',
                  }}
                >
                  Contact Us Today
                </Button>
                <Button 
                  component={Link}
                  to="/real-estate"
                  variant="outlined" 
                  size="large" 
                  sx={{ 
                    borderColor: 'white', 
                    color: 'white', 
                    px: 4,
                    py: 1.5,
                    '&:hover': { 
                      borderColor: 'white', 
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'transform 0.3s',
                  }}
                >
                  Explore Properties
                </Button>
              </Stack>
            </Box>
            
            {/* Decorative Circles */}
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -80,
                left: -80,
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)',
                zIndex: 1,
              }}
            />
          </Paper>
        </Container>
      </Box>

      {/* SECTION 11: FOOTER - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Experience the Marvellous difference
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Add keyframe animation for the moving underline */}
      <style>
        {`
          @keyframes pulseWidth {
            0%, 100% { width: 80px; }
            50% { width: 120px; }
          }
        `}
      </style>
    </Box>
  );
};

export default AboutPage;